import { useEffect, useMemo, useState } from "react";
import {
  fetchAppleMusicEnrichment,
  getAppleMusicEnrichmentKey,
} from "@/utils/appleMusicEnrichment";
import type {
  AppleMusicEnrichment,
  AppleMusicEnrichmentQuery,
  FetchAppleMusicEnrichmentOptions,
} from "@/utils/appleMusicEnrichment";

interface UseAppleMusicEnrichmentOptions extends FetchAppleMusicEnrichmentOptions {
  enabled?: boolean;
  concurrency?: number;
}

export function useAppleMusicEnrichment(
  queries: AppleMusicEnrichmentQuery[],
  options: UseAppleMusicEnrichmentOptions = {}
) {
  const [enrichmentByKey, setEnrichmentByKey] = useState<Record<string, AppleMusicEnrichment>>({});
  const [loading, setLoading] = useState(false);
  const enabled = options.enabled !== false;
  const concurrency = Math.max(1, options.concurrency || 3);

  const normalizedQueries = useMemo(() => {
    const seen = new Set<string>();
    return queries
      .filter((query) => Boolean(query.artistName && (query.releaseName || query.albumName || query.lookupType === "artist")))
      .filter((query) => {
        const key = getAppleMusicEnrichmentKey(query);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [queries]);

  const dependencyKey = useMemo(() => normalizedQueries.map(getAppleMusicEnrichmentKey).join("|"), [normalizedQueries]);

  useEffect(() => {
    if (!enabled || normalizedQueries.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const next: Record<string, AppleMusicEnrichment> = {};
      let cursor = 0;

      const worker = async () => {
        while (cursor < normalizedQueries.length) {
          const query = normalizedQueries[cursor];
          cursor += 1;
          const enrichment = await fetchAppleMusicEnrichment(query, options);
          if (enrichment) {
            next[enrichment.key] = enrichment;
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(concurrency, normalizedQueries.length) }, worker));
      if (!cancelled) {
        setEnrichmentByKey((prev) => ({ ...prev, ...next }));
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [enabled, dependencyKey, concurrency, options.ttlMs, options.appleLimit, options.itunesLimit]);

  const getEnrichment = (query: AppleMusicEnrichmentQuery) => {
    return enrichmentByKey[getAppleMusicEnrichmentKey(query)] || null;
  };

  return {
    enrichmentByKey,
    getEnrichment,
    loading,
  };
}

export default useAppleMusicEnrichment;
